/**
 * Qualification Matrix Query Hooks (Epic C-2 — Kwalificaties, Story C-2.2)
 *
 * Eigen bestand naast `useQualificationLists.ts` (Story C-2.1) — dezelfde
 * query-keys-factory + invalidatie-conventie (AD-9/AD-18). `useGrant`/
 * `useRevoke` invalideren bewust BEIDE matrix-vormen (per-medewerker èn
 * "wie kan wat") na een mutatie: eenzelfde toggle kan van beide weergaven
 * uit worden bediend (UX-C3, "zelfde mutatie, andere ingang").
 *
 * De AD-15 `dryRun`-telling is bewust GEEN React Query-hook — het is een
 * eenmalige, imperatieve leesvraag vlak vóór een mogelijke confirm-dialog
 * (`QualificationCheckbox`), niet een cachebare resource.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  qualificationsApi,
  type RevokeQualificationInput,
  type ScopedEmployeesFilters,
  type WhoCanDoWhatFilters,
} from '../../../lib/qualificationsApi';
import type { GrantQualificationInput } from '@functieloonhuis/shared';

export const qualificationMatrixKeys = {
  all: ['qualificationMatrix'] as const,
  scopedEmployees: (filters: ScopedEmployeesFilters) => [...qualificationMatrixKeys.all, 'scopedEmployees', filters] as const,
  employeeMatrix: (employeeId: number | null) => [...qualificationMatrixKeys.all, 'employeeMatrix', employeeId] as const,
  whoCanDoWhat: (filters: WhoCanDoWhatFilters | null) => [...qualificationMatrixKeys.all, 'whoCanDoWhat', filters] as const,
};

export function useScopedEmployees(filters: ScopedEmployeesFilters = {}) {
  return useQuery({
    queryKey: qualificationMatrixKeys.scopedEmployees(filters),
    queryFn: () => qualificationsApi.scopedEmployees(filters),
  });
}

export function useEmployeeQualifications(employeeId: number | null) {
  return useQuery({
    queryKey: qualificationMatrixKeys.employeeMatrix(employeeId),
    queryFn: () => qualificationsApi.employeeMatrix(employeeId!),
    enabled: employeeId !== null,
  });
}

export function useWhoCanDoWhat(filters: WhoCanDoWhatFilters | null) {
  return useQuery({
    queryKey: qualificationMatrixKeys.whoCanDoWhat(filters),
    queryFn: () => qualificationsApi.whoCanDoWhat(filters!),
    enabled: filters !== null,
  });
}

export function useGrantQualification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: GrantQualificationInput) => qualificationsApi.grant(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qualificationMatrixKeys.all });
    },
  });
}

export function useRevokeQualification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: RevokeQualificationInput) => qualificationsApi.revoke(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qualificationMatrixKeys.all });
    },
  });
}
